import { useEffect, useState } from "react";
import { ConfigProvider, Menu, Layout } from "antd";
import type { MenuProps } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import useUsuario from "../../../core/hooks/UseUsuario";
import useNotifications from "../../../core/hooks/useNotifications";
import Wordmark from "../../components/Wordmark/Wordmark";
import { getSidebarNavGroups, toAntdMenuItem, SIDEBAR_RAIL_MENU_THEME } from "./sidebarNavConfig";

const { Sider } = Layout;

/*
 * O `SidebarUserCard` foi removido daqui: o `ProfileMenu` (vidro líquido,
 * montado pelo Default.layout) ocupa exatamente este canto e traz as mesmas
 * coisas — avatar, nome, "Perfil" e a saída de sessão de verdade. Os dois
 * juntos seriam dois avatares sobrepostos no pé do rail.
 *
 * O espaço reservado continua existindo: o menu é `position: fixed`, então não
 * empurra o `Menu`, mas o padding de baixo do rail evita que o último item de
 * navegação fique atrás do avatar flutuante.
 */

type ItemType = NonNullable<MenuProps["items"]>[number];

interface SidebarProps {
  collapsed: boolean;
}

export default function DefaultLayoutSidebar({ collapsed }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const { usuario } = useUsuario();

  // Mesmo hook do sino (`NotificationBell`). Para notificações e revisão, o
  // **mesmo estado** — `useNotificationReadsStore` — então montar o hook aqui
  // não acrescenta requisição às duas: sino e Sidebar somam uma leitura de
  // `/notifications` e uma de `/review/stats` por navegação, não uma por
  // consumidor, e um "marcar como lida" no painel aparece nos dois badges no
  // mesmo render. A fila de moderação é buscada por instância (o Sidebar
  // soma UMA `GET /moderation/stats` própria quando é admin), mas o número
  // converge com o do sino via `useModerationCountersStore`, e um decremento
  // (`applyModerationDelta`) aparece nos dois sem round-trip. O hook também
  // se auto-gate por perfil administrativo, então um perfil não-admin nunca
  // dispara `GET /moderation/stats` por causa do Sidebar.
  const { moderation } = useNotifications();
  const moderationCount = moderation ? moderation.pendingComments + moderation.openReports : 0;

  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const stored = localStorage.getItem("openKeys");
    return stored ? JSON.parse(stored) : ["1"];
  });

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
    localStorage.setItem("openKeys", JSON.stringify(keys)); // ✅ persist change
  };

  useEffect(() => {
    // ✅ auto-open submenu when the path matches
    if (location.pathname.includes("home") && !openKeys.includes("1")) {
      setOpenKeys((prev) => [...prev, "1"]);
    }
  }, [location.pathname]);

  const menuItems = getSidebarNavGroups({
    profile: usuario?.profile,
    navigate,
    moderationCount,
  }).map((group, index) => ({
    key: group.key,
    type: "group" as const,
    label: group.label,
    // Frame `jwISD`'s 20px gap between stacked groups — antd has no
    // component token for inter-group spacing, and the default title
    // padding-top (8px) already covers the first group under the wordmark
    // (see the wrapper's own gap-5 below), so only groups after the first
    // need the extra 12px to reach 20px total.
    className: index > 0 ? "mt-3" : undefined,
    children: group.items.map(toAntdMenuItem),
  })) as ItemType[];

  return (
    <Sider
      width={248}
      collapsible
      collapsed={collapsed}
      trigger={null}
      theme="light"
      className="border-r"
      style={{
        background: "rgb(var(--c-sidebar-bg))",
        borderRightColor: "rgb(var(--c-sidebar-border))",
      }}
    >
      <div className="flex h-full flex-col gap-5 px-4 pb-24 pt-5">
        <Wordmark
          width={176}
          variant="dark"
          to="/home"
          label="Acertei — ir para a Home"
        />
        <ConfigProvider theme={SIDEBAR_RAIL_MENU_THEME}>
          <Menu
            mode="inline"
            theme="light"
            className="acertei-sidebar-rail-menu"
            openKeys={openKeys}
            selectedKeys={[location.pathname]}
            onOpenChange={handleOpenChange}
            style={{ flex: 1, minHeight: 0, overflowY: "auto", borderRight: 0, background: "transparent" }}
            items={menuItems}
          />
        </ConfigProvider>
      </div>
    </Sider>
  );
}
